import React from 'react';
import { cn } from '@/lib/utils';
import Text from './Text';

/**
 * The white panel everything sits in.
 *
 * Depth in this system comes from a hairline border against the grey canvas,
 * not from a drop shadow — that is what keeps a screen with twelve cards on it
 * from looking like a pile of receipts. Reach for `elevation="raised"` only
 * when something genuinely floats above the page.
 */

type Elevation = 'flat' | 'card' | 'raised';

const ELEVATIONS: Record<Elevation, string> = {
  flat: 'bg-flup-surface border border-flup-hairline',
  card: 'bg-flup-surface border border-flup-hairline shadow-flup-card',
  raised: 'bg-flup-surface border border-flup-hairline shadow-flup-raised',
};

export interface SurfaceProps extends React.HTMLAttributes<HTMLDivElement> {
  elevation?: Elevation;
  /** Tailwind padding class. Pass `false` for a flush surface (tables, lists). */
  padding?: string | false;
  radius?: 'md' | 'lg' | 'xl';
  as?: 'div' | 'section' | 'article' | 'li' | 'aside';
}

const Surface = React.forwardRef<HTMLDivElement, SurfaceProps>(
  ({ elevation = 'card', padding = 'p-5', radius = 'lg', as = 'div', className, children, ...rest }, ref) => {
    const Tag = as as React.ElementType;
    return (
      <Tag
        ref={ref as any}
        className={cn(
          ELEVATIONS[elevation] ?? ELEVATIONS.card,
          radius === 'md' ? 'rounded-flup-md' : radius === 'xl' ? 'rounded-flup-xl' : 'rounded-flup-lg',
          padding || undefined,
          className,
        )}
        {...rest}
      >
        {children}
      </Tag>
    );
  },
);

Surface.displayName = 'FlupSurface';

/**
 * A titled band inside a surface — "Customer details", "Route info". The
 * reference uses these to break a long detail panel into scannable groups.
 */
export const SurfaceSection: React.FC<{
  title?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}> = ({ title, action, className, children }) => (
  <div className={cn('rounded-flup-md border border-flup-hairline p-4', className)}>
    {title || action ? (
      <div className="flex items-center justify-between gap-3 mb-3">
        {title ? (
          <Text variant="fieldLabel" tone="tertiary" as="p">
            {title}
          </Text>
        ) : (
          <span />
        )}
        {action}
      </div>
    ) : null}
    {children}
  </div>
);

export default React.memo(Surface);
